/**
 * SOLID Review
 * S — Acceptable. 25 lines but three distinct reasons to change live here: production domain/GraphQL URL
 *     construction, AdSense public identifiers (ADSENSE_CLIENT/ADSENSE_SLOT_TOP_BANNER), and a temporary
 *     review-mode feature flag (ADSENSE_APPROVAL_MODE, explicitly commented as "TEMPORARY"). Small enough
 *     that this is low-risk, but it is genuinely three concerns bundled under "shared config."
 * O — Good. Each concern is a handful of independent consts; adding a new one doesn't touch the others.
 * L — N/A — no inheritance hierarchy.
 * I — N/A-equivalent — plain exported constants, no method surface to narrow.
 * D — Good. Reads import.meta.env directly (a Vite-runtime coupling, cast through `unknown as {env?...}`)
 *     but otherwise has zero application/infrastructure imports.
 */

// Single source of truth for the public production domain (frontend side).
// Swap PROD_DOMAIN here to retarget every production URL the frontend falls back to.
//
// NOTE: frontend/index.html's link-preview tags (og:url, og:image) are static HTML outside
// the JS bundle and cannot import this module — keep their literals in sync by hand.
export const PROD_DOMAIN = 'ocimvm.com';
export const PROD_ORIGIN = `https://${PROD_DOMAIN}`;            // https://ocimvm.com
export const PROD_GRAPHQL_HTTP_URL = `${PROD_ORIGIN}/graphql`;  // https://ocimvm.com/graphql
export const PROD_GRAPHQL_WS_URL = `wss://${PROD_DOMAIN}/graphql`;

// Google AdSense — public identifiers (also present in ads.txt and the index.html
// google-adsense-account verification meta; not secrets). Used by GoogleAdUnit for the
// front-screen banner. The in-game upper area gets its IDs from the backend
// getAdProviderConfig query instead (env-driven), so these are the front-screen source.
export const ADSENSE_CLIENT = 'ca-pub-5720396580867957';
// Dedicated FIXED 320×50 ad unit ("Ad320x50") — matches GoogleAdUnit's fixed size so the
// dashboard unit and the rendered <ins> agree (the old '4421059912' was a Responsive unit,
// whose unfilled placeholder balloons to a 384px square during review).
export const ADSENSE_SLOT_TOP_BANNER = '4169935830';

// TEMPORARY — AdSense approval. Gates the WelcomeScreen banner so it only shows while the
// site is under review. Set VITE_ADSENSE_APPROVAL=true in frontend/.env and restart the
// dev server (Vite inlines VITE_* at startup); unset to remove the front-screen ad.
export const ADSENSE_APPROVAL_MODE =
  (import.meta as unknown as { env?: Record<string, string> }).env?.['VITE_ADSENSE_APPROVAL'] === 'true';

// -----------------------------------------------------------------------------
// Environment-derived endpoints
//
// These three lived inline at module scope in MaintenanceBanner, HealthIndicator and
// LobbyPanel, each wrapped in a `c8 ignore start/end` block reading "not controllable in unit
// tests". That was true of the INLINE form only: `import.meta.env` is captured at module load
// and a test cannot reach another module's copy. As parameterised functions the same decisions
// are ordinary values, so the blocks are gone and the branches are covered (2026-08-16).
//
// The health-url logic was also duplicated verbatim between MaintenanceBanner and
// HealthIndicator — two copies of a rule that must agree, which is its own defect.
// -----------------------------------------------------------------------------

/**
 * Where to poll for backend health.
 *
 * In dev the app is served from the same host as the API, so same-origin is right. In
 * production the health endpoint is the GraphQL URL with `/graphql` swapped for `/health`.
 */
export function resolveHealthUrl(
  env: Record<string, unknown>,
  location: { protocol: string; host: string },
): string {
  if (env['DEV'] === true || env['DEV'] === 'true') {
    return `${location.protocol}//${location.host}/health`;
  }
  const http = env['VITE_GRAPHQL_HTTP_URL'];
  const base = typeof http === 'string' && http !== '' ? http : PROD_GRAPHQL_HTTP_URL;
  return base.replace('/graphql', '/health');
}

/**
 * The origin to put in a share link or QR code.
 *
 * Detected by HOSTNAME, not by the DEV flag: production serves through a Vite dev server, so
 * `import.meta.env.DEV` is true there too. A localhost origin in a QR code is unreachable from
 * the phone scanning it — which is the entire point of the QR code.
 */
export function resolveShareBase(location: { hostname: string; origin: string }): string {
  return /^(localhost|127\.|0\.0\.0\.0|\[?::1\]?)/.test(location.hostname)
    ? PROD_ORIGIN
    : location.origin;
}

